/**
 * RsvpFormModal — 게스트 참석여부 제출 폼 모달 (청첩장 톤)
 */

import { useState } from 'react'
import type { RsvpFields } from '@cugumi/shared/schemas'
import type { AttendanceStatus, MealOption } from '@cugumi/shared/schemas'
import { Modal, Input } from '@/components/ui/index.js'
import { apiFetch, getErrorMessage } from '@/lib/api.js'
import styles from './RsvpFormModal.module.css'

interface RsvpFormModalProps {
  open: boolean
  onClose: () => void
  invitationId: string
  rsvpFields?: RsvpFields
}

const attendanceOptions: { value: AttendanceStatus; label: string }[] = [
  { value: 'ATTENDING', label: '참석' },
  { value: 'NOT_ATTENDING', label: '불참' },
  { value: 'MAYBE', label: '미정' },
]

const mealOptions: { value: MealOption; label: string }[] = [
  { value: 'ADULT', label: '일반' },
  { value: 'CHILD', label: '아동' },
  { value: 'VEGETARIAN', label: '채식' },
  { value: 'NONE', label: '없음' },
]

export function RsvpFormModal({ open, onClose, invitationId, rsvpFields }: RsvpFormModalProps) {
  const [guestName, setGuestName] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [attendance, setAttendance] = useState<AttendanceStatus | ''>('')
  const [guestCount, setGuestCount] = useState(1)
  const [mealOption, setMealOption] = useState<MealOption | ''>('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const showPhone = rsvpFields?.phone !== false
  const showGuestCount = rsvpFields?.guestCount !== false
  const showMeal = rsvpFields?.meal !== false

  const handleSubmit = async () => {
    if (!guestName.trim()) {
      setError('이름을 입력하세요.')
      return
    }
    if (!attendance) {
      setError('참석 여부를 선택하세요.')
      return
    }

    setError(null)
    setSubmitting(true)

    try {
      const body: Record<string, unknown> = {
        invitationId,
        guestName: guestName.trim(),
        attendance,
      }
      if (showPhone && guestPhone.trim()) {
        body.guestPhone = guestPhone.trim()
      }
      if (showGuestCount) {
        body.guestCount = guestCount
      }
      if (showMeal && mealOption) {
        body.mealOption = mealOption
      }

      const res = await apiFetch(`/api/invitations/${invitationId}/rsvp`, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      })

      if (res.success) {
        setSubmitted(true)
      } else {
        setError(getErrorMessage(res.error))
      }
    } catch {
      setError('네트워크 오류가 발생했습니다. 다시 시도해주세요.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    onClose()
    if (submitted) {
      setTimeout(() => {
        setGuestName('')
        setGuestPhone('')
        setAttendance('')
        setGuestCount(1)
        setMealOption('')
        setSubmitted(false)
        setError(null)
      }, 200)
    }
  }

  if (!open) return null

  return (
    <Modal open={open} onClose={handleClose} size="sm" className={styles.modalPanel}>
      {submitted ? (
        <div className={styles.successView}>
          <div className={styles.successIcon}>✓</div>
          <p className={styles.successMessage}>참석 여부가 전달되었습니다</p>
          <p className={styles.successSub}>소중한 답변 감사합니다</p>
          <button className={styles.closeButton} onClick={handleClose}>
            닫기
          </button>
        </div>
      ) : (
        <>
          <div className={styles.formHeader}>
            <h2 className={styles.formTitle}>참석 여부</h2>
            <div className={styles.formDivider} />
          </div>

          <div className={styles.form}>
            {/* 성함 */}
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                성함 <span className={styles.required}>*</span>
              </label>
              <Input
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="이름을 입력하세요"
                maxLength={255}
              />
            </div>

            {/* 연락처 */}
            {showPhone && (
              <div className={styles.field}>
                <label className={styles.fieldLabel}>연락처</label>
                <Input
                  type="tel"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder="010-1234-5678"
                />
              </div>
            )}

            {/* 참석 여부 — pill 버튼 */}
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                참석 여부 <span className={styles.required}>*</span>
              </label>
              <div className={styles.pillGroup}>
                {attendanceOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.pill} ${attendance === opt.value ? styles.pillSelected : ''}`}
                    onClick={() => setAttendance(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 참석 인원 */}
            {showGuestCount && (
              <div className={styles.field}>
                <label className={styles.fieldLabel}>참석 인원</label>
                <Input
                  type="number"
                  value={String(guestCount)}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10)
                    if (!isNaN(v) && v >= 1 && v <= 10) setGuestCount(v)
                  }}
                  min={1}
                  max={10}
                />
              </div>
            )}

            {/* 식사 — pill 버튼 */}
            {showMeal && (
              <div className={styles.field}>
                <label className={styles.fieldLabel}>식사</label>
                <div className={styles.pillGroup}>
                  {mealOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`${styles.pill} ${mealOption === opt.value ? styles.pillSelected : ''}`}
                      onClick={() => setMealOption(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && <p className={styles.error}>{error}</p>}

            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? '제출 중...' : '제출하기'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}
